import { FormStatus, ProgressIndicator } from '@dnb/eufemia';
import { BAID_MATCHER, BSNID_MATCHER } from '@portals/shared/common/matchers';
import { ServiceNowBusinessApplicationDto, ServiceNowServiceOfferingDto } from '@portals/shared/portal/SupportTicket';
import { useEffect } from 'react';
import useSWR from 'swr';

import { ApiError } from '@/request';

import ServiceNowIdentifierInfoCard from './ServiceNowIdentifierInfoCard';

interface ServiceNowIdentifierViewerProps {
  serviceNowIdentifier: string | null | undefined;
  onChange?: (data: ServiceNowBusinessApplicationDto | ServiceNowServiceOfferingDto | null) => void;
  onError?: (message: string) => void;
}

/**
 * @param onError Optional callback to handle errors. If provided, errors will not be displayed within the component.
 */
export default function ServiceNowIdentifierViewer({
  serviceNowIdentifier,
  onChange,
  onError,
}: ServiceNowIdentifierViewerProps) {
  const isValidBAIdentifier = serviceNowIdentifier ? BAID_MATCHER.test(serviceNowIdentifier) : false;
  const isValidBSNIdentifier = serviceNowIdentifier ? BSNID_MATCHER.test(serviceNowIdentifier) : false;

  const { data, error, isValidating } = useSWR<ServiceNowBusinessApplicationDto | ServiceNowServiceOfferingDto>(
    isValidBAIdentifier
      ? `/service-now/business-applications/${serviceNowIdentifier}`
      : isValidBSNIdentifier
        ? `/service-now/service-offerings/${serviceNowIdentifier}`
        : null,
    {
      onError: () => {}, // Handle error locally instead of global error page
    },
  );

  useEffect(() => {
    if (!onChange || isValidating) return;
    if (data && (isValidBAIdentifier || isValidBSNIdentifier)) {
      onChange(data);
    } else {
      onChange(null);
    }
  }, [data, isValidating, isValidBAIdentifier, isValidBSNIdentifier]);

  useEffect(() => {
    if (error && onError) onError(getErrorMessage(error));
  }, [error]);

  if (error && !onError) return <FormStatus bottom="large" stretch text={getErrorMessage(error)} />;
  if (isValidating) return <ProgressIndicator size="small" type="linear" />;
  if (!data) return null;

  return <ServiceNowIdentifierInfoCard data={data} />;
}

function getErrorMessage(error: unknown): string {
  return ApiError.isApiError(error) ? error.message : 'Something went wrong. Please try again later.';
}
